import React, { useState, useEffect } from 'react';
import { Camera, X, Maximize2, ChevronDown, Sparkles, AlertCircle, Loader2, Key, Download, Plus, User, UserPlus, RefreshCw, ChevronRight, ChevronLeft, Lock, ExternalLink } from 'lucide-react';
import { CreativeBrief, CampaignImage, AppStep, SubjectProfile } from './types';
import { generateCampaignConcepts, generateTwinImage, setGeminiApiKey } from './services/geminiService';

const REALISM_PROFILES = [
  "Unprocessed RAW (Phase One)", "Standard Studio", "Raw Cinema", "Ultra-HD Commercial", "Analog Film (Portra 400)", "Hyper-Realistic", "Shot on iPhone", "Soft Focus Editorial", "Candid Street", "High-Contrast Noir", "Magazine Print", "80mm Prime", "Vintage Polaroid",
  "Medium Format Digital", "High-Resolution Catalog", "High-End Retouch", "Lifestyle Candid", "Commercial Sharp", "Film Grain Cinema", "Raw Documentary", "Glossy Editorial"
];

const LIGHTING_OPTIONS = [
  "Golden Hour (Soft, Warm)", "Rembrandt (Dramatic Shadow)", "Butterfly (Glamour)", "High Key (Bright, Clean)", "Low Key (Dark, Moody)", "Neon / Cyberpunk", "Softbox Studio", "Natural Window Light", "Hard Flash (Street Style)", "Cinematic Blue Hour",
  "High Noon (Crisp Shadows)", "Overcast Day (Diffused)", "Studio Beauty Dish", "City Street Lamps", "Warm Interior Ambient", "Color-Gel Accent", "Backlit (Halo Effect)", "Soft Morning Glow"
];

const LENS_OPTIONS = [
  "85mm Prime (Portrait Bokeh)", "35mm (Natural, Classic)", "24mm Wide (Dramatic)", "50mm Standard", "135mm (Compressed)", "Anamorphic Cinematic", "Fisheye (Experimental)", "Macro (Hyper-Detail)",
  "28mm Street Perspective", "100mm Macro (Product Focus)", "55mm Narrative", "Vintage Leica 35mm", "Modern Zeiss Clarity"
];

const BODY_TYPE_OPTIONS = ["As in photo", "Slim / Lean", "Athletic / Toned", "Curvy / Hourglass", "Muscular / Built", "Statuesque / Tall", "Petite", "Natural / Average", "Soft & Full", "Rugged / Broad"];

const VIBE_OPTIONS = [
  "Standard / Realistic", "Old Money", "Quiet Luxury", "Streetwear", "Baddie", "Dark Academia", "Cyberpunk", "Minimalist", "Power / Executive", "Y2K Aesthetic", "Boho Luxe",
  "Athletic Sporty", "Corporate Professional", "Desert Nomad", "Tropical Resort", "Industrial Minimalist", "Cozy Hygge", "Preppy Ivy League", "Avant-Garde Fashion", "Gilded Age Luxury"
];

const SCENE_OPTIONS = [
  "Minimalist Home Office", "Standing Desk Workspace", "Modern Startup Hub", "Industrial Coworking Loft", "Podcast Recording Studio", "Tech Workshop Garage", "Rooftop Garden Workspace", "Brick-Walled Creative Studio", "High-Rise Balcony Office", "Modern Boardroom", "Digital Nomad Beach Cafe", "Quiet Attic Library", "Sunny Conservatory Office", "Boutique Hotel Lobby", "First-Class Airport Lounge", "Architectural Studio", "Whiteboard Strategy Session", "Professional Photo Studio", "Video Call Background (Bookshelf)", "Minimalist Zen Room",
  "Cozy Morning Kitchen", "Kitchen Island / Breakfast Bar", "Modern Living Room", "Small Sunlit Garden", "Semi-Detached House Front", "Suburban Driveway", "Contemporary Townhouse Entry", "Local Coffee Shop / Cafe", "Neighborhood Park Bench", "Suburban Tree-Lined Street", "Modern Farmhouse Kitchen", "Weekend Farmers Market", "Urban High Street", "Grocery Store Aisle", "Flower Shop Entrance", "Cozy Bedroom Corner", "Contemporary Dining Room", "Small Urban Bistro", "Greenhouse / Solarium", "University Campus Lawn",
  "Private Jet", "Luxury Car Interior", "Skyline Penthouse", "Modern Corporate Office", "Tennis Court", "Paris (Eiffel Tower)", "Dubai (Desert Dunes)", "Santorini Blue Dome", "Superyacht Deck", "Art Gallery Opening", "Alpine Ski Cabin", "Neon Tokyo Street", "Coastal Villa Terrace", "Vineyard at Sunset", "Luxury Day Spa", "Sailing Yacht Salon", "Modern Library Wing", "Chic Rooftop Bar", "Industrial Art Museum", "Luxury Train Suite", "Private Cinema Room", "Designer Fashion Boutique", "Modern Wellness Center", "Equestrian Stable Lounge"
];

const OUTFIT_OPTIONS = [
  "As in photo", "Power Suit", "Tailored Three-Piece", "Casual Chic", "Luxury Athleisure", "Tennis Outfit", "Satin Evening Wear", "Biker Leather", "Minimalist Linen", "Trench Coat Couture",
  "Cashmere Loungewear", "Resort Wear Luxe", "Smart Business Casual", "Evening Black Tie", "Designer Activewear", "Silk Pajama Set", "Overstated Winter Fur", "Minimalist Tailoring", "Denim & Diamonds", "Equestrian Gear", "Yacht Club Whites"
];

const PHASE_TABS = [
  { id: 0, label: "Identity", phase: "01" },
  { id: 1, label: "Creative", phase: "02" },
  { id: 2, label: "Likeness", phase: "03" },
  { id: 3, label: "Optics", phase: "04" }
];

const PhaseHeader: React.FC<{ phase: string; title: string }> = ({ phase, title }) => (
  <div className="flex flex-col items-center gap-2 mb-12 text-center">
    <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase {phase}</span>
    <h2 className="text-2xl md:text-3xl font-display font-semibold text-white tracking-[1px]">{title}</h2>
    <div className="w-12 h-[1px] bg-[#dffe00]/30 mt-4" />
  </div>
);

const StudioDropdown: React.FC<{ label: string; value: string; options: any[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-3 group">
    <label className="text-[9px] font-bold uppercase tracking-widest text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-4 px-5 text-[11px] text-white/80 appearance-none focus:outline-none focus:border-[#dffe00]/50 focus:bg-white/[0.06] transition-all cursor-pointer hover:border-white/20"
      >
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt : opt.label;
          return <option key={val} value={val} className="bg-black">{lbl}</option>;
        })}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20"><ChevronDown className="w-4 h-4" /></div>
    </div>
  </div>
);

const AnchorSlot: React.FC<{ label: string; value: string | null; onUpload: (data: string | null) => void }> = ({ label, value, onUpload }) => (
  <div className="flex flex-col gap-3 flex-1">
    <label className="text-[9px] font-bold uppercase tracking-widest text-center text-[#dffe00]/50">{label}</label>
    <div className="relative aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group cursor-pointer hover:border-white/20 transition-all">
      {value ? (
        <>
          <img src={value} className="w-full h-full object-cover" alt={label} />
          <button onClick={(e) => { e.preventDefault(); onUpload(null); }} className="absolute top-2 right-2 p-2 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/10 group-hover:text-[#dffe00]/40 transition-colors cursor-pointer">
          <Plus className="w-6 h-6 stroke-[1px]" />
          <span className="text-[9px] uppercase tracking-widest">Upload Portrait</span>
          <input type="file" className="hidden" accept="image/*" onChange={async e => {
             const file = e.target.files?.[0];
             if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (ev) => onUpload(ev.target?.result as string);
             }
          }} />
        </label>
      )}
    </div>
  </div>
);

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('landing');
  const [activeTab, setActiveTab] = useState(0);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [activeConceptPrompt, setActiveConceptPrompt] = useState<string | null>(null);

  const [brief, setBrief] = useState<CreativeBrief>({
    subjects: [
      { anchorImages: { frontal: null } },
      { anchorImages: { frontal: null } }
    ],
    vibe: VIBE_OPTIONS[0],
    scene: SCENE_OPTIONS[0],
    outfit: OUTFIT_OPTIONS[0],
    hairStyle: "As in photo",
    makeupPalette: "As in photo",
    skinStyle: "Natural & Realistic",
    realismTuning: "Unprocessed RAW (Phase One)",
    bodyType: "As in photo",
    bustLine: "As in photo",
    waistRatio: "As in photo",
    hipsSculpt: "As in photo",
    cameraAngle: "Standard Eye Level",
    lighting: LIGHTING_OPTIONS[0],
    lens: LENS_OPTIONS[0],
    aspectRatio: "9:16",
    numImages: 4,
    customPrompt: "",
    imageSize: "1K"
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  useEffect(() => {
    // Check if env key exists, if so we might skip the gate or pre-fill
    // But per design request, we show the gate. The user can override.
    if (process.env.API_KEY) {
       // Optional: Auto-login if environment key is present? 
       // For this design, we'll let the user enter it or just press activate if we pre-filled it.
       // However, to match the visual exactly, we start at 'landing'.
    }
  }, []);

  const handleActivateStudio = () => {
    if (!apiKeyInput && !process.env.API_KEY) {
      alert("Please enter a valid Gemini API Key to continue.");
      return;
    }
    if (apiKeyInput) {
      setGeminiApiKey(apiKeyInput);
    }
    setStep('studio');
  };

  const updateAnchor = (subjectIndex: number, value: string | null) => {
    const newSubjects = [...brief.subjects] as [SubjectProfile, SubjectProfile];
    newSubjects[subjectIndex].anchorImages.frontal = value;
    setBrief(p => ({ ...p, subjects: newSubjects }));
  };

  const generateSingleImage = async (id: string, conceptPrompt: string) => {
    setImages(prev => prev.map(p => p.id === id ? { ...p, status: 'generating', error: undefined } : p));
    try {
      const url = await generateTwinImage(brief, conceptPrompt);
      setImages(prev => prev.map(p => p.id === id ? { ...p, status: 'completed', imageUrl: url } : p));
    } catch (e: any) {
      console.error(e);
      if (e.message === "KEY_RESET_REQUIRED") {
        setStep('landing');
        alert("Studio identity reset required. Please update your API key.");
        return;
      }
      setImages(prev => prev.map(p => p.id === id ? { ...p, status: 'failed', error: e.message } : p));
    }
  };

  const initiateProduction = async () => {
    if (!brief.subjects[0].anchorImages.frontal || !brief.subjects[1].anchorImages.frontal) {
      alert("Please upload both identity portraits to initiate Duo Production.");
      setActiveTab(0);
      return;
    }
    
    setIsGenerating(true);
    setImages([]); 
    setActiveConceptPrompt(null);

    try {
      const generated = await generateCampaignConcepts(brief);
      if (!generated || generated.length === 0) throw new Error("Could not produce duo concept.");
      
      const conceptPrompt = generated[0].prompt;
      setActiveConceptPrompt(conceptPrompt);

      const initialImages: CampaignImage[] = Array.from({ length: brief.numImages }, (_, i) => ({
        id: `img-${i}`, themeIndex: generated[0].themeIndex, status: 'idle'
      }));
      setImages(initialImages);

      // Trigger generation for all slots
      for (const img of initialImages) {
        await generateSingleImage(img.id, conceptPrompt);
      }
    } catch (e: any) {
      console.error(e);
      if (e.message === "KEY_RESET_REQUIRED") {
        setStep('landing');
        alert("Studio identity reset required. Please update your API key.");
      } else {
        alert(`Production Error: ${e.message}`);
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = async (id: string) => {
    if (!activeConceptPrompt) return;
    await generateSingleImage(id, activeConceptPrompt);
  };

  const nextTab = () => setActiveTab(prev => Math.min(prev + 1, PHASE_TABS.length - 1));
  const prevTab = () => setActiveTab(prev => Math.max(prev - 1, 0));

  if (step === 'landing') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white text-center font-display">
        <div className="max-w-md w-full flex flex-col items-center animate-fade-in">
          
          {/* Lock Icon */}
          <div className="w-20 h-20 bg-white/[0.05] rounded-[32px] flex items-center justify-center border border-white/10 mb-8 shadow-2xl">
            <Lock className="w-8 h-8 text-white/50 stroke-[1.5px]" />
          </div>

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-semibold text-white tracking-[0.5px] mb-2">
            AI Twin Studio: <span className="text-[#dffe00]">Duo</span>
          </h1>

          {/* Subtitle */}
          <p className="text-[#666] text-[10px] font-bold uppercase tracking-[0.25em] mb-12">
            Connect Studio Access Key to Begin
          </p>

          {/* Input */}
          <div className="w-full relative mb-4">
            <input 
              type="password"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              placeholder="Paste Gemini API Key"
              className="w-full bg-white/[0.05] border border-white/10 rounded-xl py-5 px-6 text-[13px] text-center text-white placeholder-white/20 focus:outline-none focus:border-[#dffe00]/50 focus:bg-white/[0.08] transition-all"
            />
          </div>

          {/* Button */}
          <button 
            onClick={handleActivateStudio}
            className="w-full py-5 bg-white text-black text-[11px] font-bold uppercase tracking-[0.2em] rounded-xl hover:bg-[#dffe00] transition-all duration-300 mb-16 shadow-[0_10px_40px_-10px_rgba(255,255,255,0.1)] hover:shadow-[0_10px_40px_-10px_rgba(223,254,0,0.3)]"
          >
            Activate Studio
          </button>

          {/* Footer Link */}
          <div className="w-full border-t border-white/[0.08] pt-8 flex justify-center">
            <a 
              href="https://aistudio.google.com/app/api-keys" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/30 text-[9px] uppercase tracking-widest hover:text-white transition-colors group"
            >
              Get your free API key from Google
              <ExternalLink className="w-3 h-3 group-hover:text-[#dffe00] transition-colors" />
            </a>
          </div>

        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white selection:bg-[#dffe00] selection:text-black pb-32 font-sans">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-6 flex justify-between items-center bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setStep('landing')}>
          <span className="font-display font-semibold text-[10px] tracking-[1px] uppercase">AI Twin Studio: Duo</span>
        </div>
        <button className="text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors" onClick={() => setStep('landing')}>Exit Studio</button>
      </nav>

      <main className="max-w-5xl mx-auto px-6 pt-32">
        <div className="w-full mb-16 flex justify-center">
          <div className="flex gap-4 md:gap-12 items-center bg-white/[0.02] border border-white/5 rounded-full px-8 py-3">
            {PHASE_TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest transition-all relative group py-2
                  ${activeTab === tab.id ? 'text-[#dffe00]' : 'text-white/20 hover:text-white/40'}
                `}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center border text-[8px] transition-colors
                  ${activeTab === tab.id ? 'bg-[#dffe00] text-black border-[#dffe00]' : 'border-white/10 text-white/20'}
                `}>
                  {tab.id + 1}
                </span>
                <span className="hidden md:inline">{tab.label}</span>
                {activeTab === tab.id && (
                  <div className="absolute -bottom-1 left-0 w-full h-[1px] bg-[#dffe00] shadow-[0_0_8px_rgba(223,254,0,0.5)]" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-[500px]">
          {activeTab === 0 && (
            <section className="animate-fade-in flex flex-col items-center">
              <PhaseHeader phase="01" title="Identity Portraits" />
              <p className="text-white/30 text-[10px] uppercase tracking-widest mb-12">Upload portraits of the two people for synthesis</p>
              <div className="w-full flex flex-col md:flex-row gap-8 justify-center mb-16">
                <AnchorSlot label="Person 1" value={brief.subjects[0].anchorImages.frontal} onUpload={val => updateAnchor(0, val)} />
                <AnchorSlot label="Person 2" value={brief.subjects[1].anchorImages.frontal} onUpload={val => updateAnchor(1, val)} />
              </div>
              <button onClick={nextTab} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#dffe00] hover:text-white transition-all group">
                Creative Direction <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </section>
          )}

          {activeTab === 1 && (
            <section className="animate-fade-in flex flex-col items-center">
              <PhaseHeader phase="02" title="Creative Direction" />
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <StudioDropdown label="Vibe" value={brief.vibe} options={VIBE_OPTIONS} onChange={v => setBrief(p => ({ ...p, vibe: v }))} />
                <StudioDropdown label="Location" value={brief.scene} options={SCENE_OPTIONS} onChange={v => setBrief(p => ({ ...p, scene: v }))} />
                <StudioDropdown label="Attire" value={brief.outfit} options={OUTFIT_OPTIONS} onChange={v => setBrief(p => ({ ...p, outfit: v }))} />
              </div>
              <div className="flex gap-12">
                <button onClick={prevTab} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-white/20 hover:text-white transition-all group">
                  <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Identity
                </button>
                <button onClick={nextTab} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#dffe00] hover:text-white transition-all group">
                  Twin Likeness <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 2 && (
            <section className="animate-fade-in flex flex-col items-center">
              <PhaseHeader phase="03" title="Twin Likeness" />
              <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-10 mb-16">
                <StudioDropdown label="Hair Style" value={brief.hairStyle} options={["As in photo", "Side Parted Classic", "Textured Crop", "Polished Bun", "Loose Beach Waves", "Braided High-Fashion", "Retro Pompadour", "Buzz Cut Professional", "Mid-Length Flow", "Sleek Fade", "Soft Waves", "Slick Back", "Classic Blowout", "Long Textured"]} onChange={v => setBrief(p => ({ ...p, hairStyle: v }))} />
                <StudioDropdown label="Makeup / Grooming" value={brief.makeupPalette} options={["As in photo", "Sun-Kissed Glow", "High-Fashion Matte", "Dewy Glass Skin", "Subtle Bronze", "Vogue Defined Contour", "Clean Professional", "Masculine Groomed", "Ethereal / Soft Focus", "Soft Glam", "Natural", "Clean Shaven", "Heavy Stubble"]} onChange={v => setBrief(p => ({ ...p, makeupPalette: v }))} />
                <StudioDropdown label="Skin Dermis" value={brief.skinStyle} options={["Natural & Realistic", "Radiant Glow", "Ultra-Smooth Catalog", "Velvet Matte", "Pore-Perfect 8K", "Slightly Freckled", "Luminous Satin", "Flawless Airbrushed", "Dewy / Radiant"]} onChange={v => setBrief(p => ({ ...p, skinStyle: v }))} />
                <StudioDropdown label="Realism Profile" value={brief.realismTuning} options={REALISM_PROFILES} onChange={v => setBrief(p => ({ ...p, realismTuning: v }))} />
                <StudioDropdown label="Body Type" value={brief.bodyType} options={BODY_TYPE_OPTIONS} onChange={v => setBrief(p => ({ ...p, bodyType: v }))} />
                <StudioDropdown label="Shoulder / Bust" value={brief.bustLine} options={["As in photo", "Natural", "Broad", "Slim", "Athletic", "Petite"]} onChange={v => setBrief(p => ({ ...p, bustLine: v }))} />
                <StudioDropdown label="Waist Ratio" value={brief.waistRatio} options={["As in photo", "Snatched", "Natural", "Athletic", "Defined V-Taper"]} onChange={v => setBrief(p => ({ ...p, waistRatio: v }))} />
                <StudioDropdown label="Physique / Hips" value={brief.hipsSculpt} options={["As in photo", "Curvy", "Athletic", "Lean", "Sturdy", "Tone-Defined"]} onChange={v => setBrief(p => ({ ...p, hipsSculpt: v }))} />
              </div>
              <div className="flex gap-12">
                <button onClick={prevTab} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-white/20 hover:text-white transition-all group">
                  <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Creative
                </button>
                <button onClick={nextTab} className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-[#dffe00] hover:text-white transition-all group">
                  Production Optics <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </section>
          )}

          {activeTab === 3 && (
            <section className="animate-fade-in flex flex-col items-center">
              <PhaseHeader phase="04" title="Production Optics" />
              <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-8 mb-24">
                <StudioDropdown label="Lighting Rig" value={brief.lighting} options={LIGHTING_OPTIONS} onChange={v => setBrief(p => ({ ...p, lighting: v }))} />
                <StudioDropdown label="Technical Lens" value={brief.lens} options={LENS_OPTIONS} onChange={v => setBrief(p => ({ ...p, lens: v }))} />
                <StudioDropdown label="Camera Angle" value={brief.cameraAngle} options={["Standard Eye Level", "Low Angle Hero", "High Angle Soft", "Side Profile", "Three-Quarter View", "Over the Shoulder"]} onChange={v => setBrief(p => ({ ...p, cameraAngle: v }))} />
                <StudioDropdown 
                  label="Frame Aspect" 
                  value={brief.aspectRatio} 
                  options={[
                    {value:"9:16", label:"9:16 Portrait (Mobile)"}, 
                    {value:"3:4", label:"3:4 Vertical"},
                    {value:"1:1", label:"1:1 Square (Social)"}, 
                    {value:"4:3", label:"4:3 Landscape"},
                    {value:"16:9", label:"16:9 Cinematic"}
                  ]} 
                  onChange={v => setBrief(p => ({ ...p, aspectRatio: v as any }))} 
                />
              </div>

              <div className="w-full flex flex-col items-center border-t border-white/5 pt-16">
                 <button onClick={initiateProduction} disabled={isGenerating} className="px-20 py-8 bg-[#dffe00] text-black text-[11px] font-bold uppercase tracking-[0.4em] rounded-full hover:bg-white hover:scale-105 transition-all duration-500 disabled:opacity-30 shadow-[0_20px_50px_rgba(223,254,0,0.3)]">
                   {isGenerating ? "Synthesizing Duo Assets..." : "Initiate Duo Production"}
                 </button>
                 {isGenerating && <p className="mt-8 text-[9px] uppercase tracking-widest text-white/30 animate-pulse">Running Duo identity sync...</p>}
                 <button onClick={prevTab} className="mt-12 flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.3em] text-white/20 hover:text-white transition-all group">
                   <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Twin Likeness
                 </button>
              </div>
            </section>
          )}
        </div>

        {images.length > 0 && (
          <section className="flex flex-col items-center pt-32 pb-24 animate-fade-in border-t border-white/5 mt-32">
             <h2 className="text-2xl font-display font-semibold text-white mb-16 tracking-[1px]">Master Output</h2>
             <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {images.map(img => (
                  <div key={img.id} className="aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-[32px] overflow-hidden group relative flex items-center justify-center shadow-2xl">
                    {img.status === 'completed' ? (
                      <>
                        <img src={img.imageUrl} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt="Master" />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-4 backdrop-blur-md">
                          <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-4 bg-white text-black rounded-full hover:scale-110 transition-transform"><Maximize2 className="w-5 h-5" /></button>
                          <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = 'twin-duo.png'; link.click(); }} className="p-4 bg-[#dffe00] text-black rounded-full hover:scale-110 transition-transform"><Download className="w-5 h-5" /></button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-4 text-center px-6">
                         {img.status === 'failed' ? (
                           <>
                             <AlertCircle className="w-8 h-8 text-red-500/50" />
                             <div className="flex flex-col gap-1">
                               <span className="text-[10px] uppercase font-bold text-red-500/80">Synthesis Failed</span>
                               <span className="text-[8px] text-white/20 max-w-[140px] truncate" title={img.error}>{img.error || 'Unknown Error'}</span>
                             </div>
                             <button 
                               onClick={() => handleRetry(img.id)}
                               className="mt-2 flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full text-[9px] uppercase tracking-widest hover:bg-white/10 transition-colors"
                             >
                               <RefreshCw className="w-3 h-3" />
                               Retry Slot
                             </button>
                           </>
                         ) : (
                           <>
                             <div className="relative">
                               <Loader2 className="w-10 h-10 text-[#dffe00] animate-spin opacity-50" />
                               <Sparkles className="w-4 h-4 text-[#dffe00] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                             </div>
                             <span className="text-[8px] uppercase tracking-[0.2em] text-[#dffe00]/40">
                               {img.status === 'generating' ? 'Fabricating Identity' : 'In Queue'}
                             </span>
                           </>
                         )}
                      </div>
                    )}
                  </div>
                ))}
             </div>
          </section>
        )}
      </main>

      <footer className="w-full py-6 mt-20 border-t border-white/5 flex items-center justify-center">
        <button 
          onClick={() => setStep('landing')} 
          className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/20 hover:text-white transition-colors"
        >
          <Key className="w-3 h-3" />
          <span>Update API Key</span>
        </button>
      </footer>

      {previewImage && <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8" onClick={() => setPreviewImage(null)}><img src={previewImage} className="max-w-full max-h-full object-contain rounded-2xl shadow-[0_0_100px_rgba(223,254,0,0.2)]" alt="Preview" /></div>}
    </div>
  );
};

export default App;